import { useEffect, useState } from "react";
import { PlayerAvatar } from "./PlayerAvatar";
import { setsSummary } from "./helpers";
import { api } from "../lib/api";
import { MapPin, Clock, Sparkles, Star } from "lucide-react";

function TeamLine({ ids, playersMap, winner }) {
  return (
    <div className={`flex items-center gap-2 ${winner ? "" : "opacity-80"}`}>
      <div className="flex -space-x-2">
        {ids.map((id) => (
          <PlayerAvatar key={id} id={id} name={playersMap[id]?.name} size={34} />
        ))}
      </div>
      <div className="min-w-0">
        {ids.map((id) => (
          <div
            key={id}
            className={`text-sm leading-tight truncate ${
              winner ? "font-bold text-white" : "font-medium text-slate-300"
            }`}
          >
            {playersMap[id]?.short_name}
          </div>
        ))}
      </div>
    </div>
  );
}

export function ScheduleView({ matchday, playersMap }) {
  const [pred, setPred] = useState(null);
  const [mvp, setMvp] = useState(null);

  useEffect(() => {
    api.get("/predictions/next").then((r) => setPred(r.data)).catch(() => {});
    api.get("/mvp").then((r) => setMvp(r.data || [])).catch(() => {});
  }, [matchday?.number]);

  if (!matchday) return null;

  const predByCourt = {};
  if (pred?.matches) pred.matches.forEach((m) => { predByCourt[m.court] = m; });
  const lastMvp = mvp && mvp.length ? mvp[mvp.length - 1] : null;

  const dateFmt = (d) => {
    if (!d) return "Data da definire";
    try {
      return new Date(d).toLocaleDateString("it-IT", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return d;
    }
  };

  return (
    <div className="space-y-6" data-testid="schedule-view">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">
            Prossima Giornata
          </div>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-white">
            Giornata {matchday.number}
          </h2>
        </div>
        <div className="flex items-center gap-2 text-slate-400 text-sm capitalize">
          <Clock className="w-4 h-4 text-emerald-400" />
          {dateFmt(matchday.date)}
        </div>
      </div>

      {(lastMvp || pred?.narrative) && (
        <div className="grid md:grid-cols-5 gap-4">
          {lastMvp && (
            <div className="md:col-span-2 ap-glass rounded-2xl p-4 flex items-center gap-3" data-testid="mvp-card">
              <div className="relative">
                <PlayerAvatar id={lastMvp.player_id} name={lastMvp.name} size={46} />
                <Star className="absolute -top-1.5 -right-1.5 w-5 h-5 text-amber-400" fill="currentColor" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-amber-400">
                  MVP Giornata {lastMvp.matchday}
                </div>
                <div className="font-bold text-white truncate">{lastMvp.name}</div>
                <div className="font-mono text-xs text-slate-400">
                  {lastMvp.points} pt · {lastMvp.games_diff > 0 ? "+" : ""}{lastMvp.games_diff} games
                </div>
              </div>
            </div>
          )}
          {pred?.narrative && (
            <div className="md:col-span-3 ap-glass rounded-2xl p-4" data-testid="prediction-card">
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles className="w-4 h-4 text-fuchsia-400" />
                <span className="text-[10px] uppercase tracking-widest text-fuchsia-400">
                  Pronostico AI
                </span>
              </div>
              <p className="text-sm text-slate-200 leading-relaxed">{pred.narrative}</p>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {matchday.matches
          .slice()
          .sort((a, b) => a.court - b.court)
          .map((m, idx) => {
            const sets = m.sets || [];
            const s1 = sets.filter((s) => s.g1 > s.g2).length;
            const s2 = sets.filter((s) => s.g2 > s.g1).length;
            const done = m.status === "completed";
            return (
              <div
                key={m.id}
                data-testid={`court-card-${m.court}`}
                className="group ap-card overflow-hidden ap-rise"
                style={{ animationDelay: `${idx * 0.07}s` }}
              >
                <div className="relative px-4 py-3 bg-gradient-to-r from-blue-950 to-emerald-950/60 border-b border-slate-800 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-emerald-400" />
                    <span className="font-display font-bold uppercase tracking-wide text-white">
                      Campo {m.court}
                    </span>
                  </div>
                  <span className="font-mono text-xs text-slate-300">{m.time}</span>
                </div>

                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <TeamLine ids={m.team1} playersMap={playersMap} winner={done && s1 > s2} />
                    {done && (
                      <span className="font-mono font-black text-2xl text-emerald-400">{s1}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-px flex-1 bg-slate-800" />
                    <span className="font-display text-xs text-slate-500 uppercase">vs</span>
                    <div className="h-px flex-1 bg-slate-800" />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <TeamLine ids={m.team2} playersMap={playersMap} winner={done && s2 > s1} />
                    {done && (
                      <span className="font-mono font-black text-2xl text-emerald-400">{s2}</span>
                    )}
                  </div>

                  <div className="pt-2 border-t border-slate-800 flex items-center justify-between">
                    {done ? (
                      <span className="font-mono text-xs text-slate-400">{setsSummary(sets)}</span>
                    ) : predByCourt[m.court] ? (
                      <span className="flex items-center gap-1.5 text-xs">
                        <Sparkles className="w-3 h-3 text-fuchsia-400" />
                        <span className="text-slate-400">Favorito:</span>
                        <span className="font-semibold text-fuchsia-300">
                          {predByCourt[m.court].favorite === "team1"
                            ? predByCourt[m.court].team1.join("/")
                            : predByCourt[m.court].favorite === "team2"
                            ? predByCourt[m.court].team2.join("/")
                            : "Equilibrio"}
                        </span>
                        <span className="font-mono text-slate-500">
                          {predByCourt[m.court].confidence}%
                        </span>
                      </span>
                    ) : (
                      <span className="text-xs font-semibold uppercase tracking-wide text-amber-400">
                        Da giocare
                      </span>
                    )}
                    {m.notes && (
                      <span className="text-[11px] text-slate-500 italic truncate max-w-[45%]">
                        {m.notes}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
